import React from 'react';

const ResultDetailsModal = ({ result, isOpen, onClose }) => {
    if (!isOpen || !result) return null;

    const getScoreColor = (score) => {
        if (score >= 80) return 'from-green-500 to-emerald-600';
        if (score >= 60) return 'from-yellow-500 to-amber-600';
        return 'from-red-500 to-pink-600';
    };

    const getPerformanceText = (score) => {
        if (score >= 80) return 'Result';
        if (score >= 60) return 'Result!';
        return 'Result';
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto animate-fade-in">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div 
                    className="fixed inset-0 transition-opacity duration-300 ease-in-out" 
                    aria-hidden="true"
                    onClick={onClose}
                >
                    <div className="absolute inset-0 bg-gray-600 opacity-75 backdrop-blur-sm"></div>
                </div>

                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full animate-scale-in">
                    <div className="bg-gradient-to-r from-green-600 to-emerald-700 px-6 py-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-bold text-white flex items-center">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Test Results
                            </h3>
                            <button 
                                onClick={onClose}
                                className="text-white hover:text-green-200 transition-colors duration-200"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>
                    
                    <div className="bg-white px-6 pt-6 pb-4">
                        <div className="text-center mb-6">
                            <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r ${getScoreColor(result.cbt_total_score)} mb-4`}>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">{getPerformanceText(result.cbt_total_score)}</h2>
                            <p className="text-gray-600">Your test has been completed successfully</p>
                        </div>

                        <div className="space-y-4 mb-6">
                            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <span className="font-medium text-gray-700">Pass Code:</span>
                                        <p className="text-gray-900 font-mono mt-1">{result.cbt_pass_code}</p>
                                    </div>
                                    <div>
                                        <span className="font-medium text-gray-700">Test ID:</span>
                                        <p className="text-gray-900 mt-1">{result.cbt_test_id}</p>
                                    </div>
                                    <div className="col-span-2">
                                        <span className="font-medium text-gray-700">Completed On:</span>
                                        <p className="text-gray-900 mt-1">
                                            {new Date(result.created_at).toLocaleString('en-US', {
                                                year: 'numeric', 
                                                month: 'short', 
                                                day: 'numeric',
                                                hour: '2-digit', 
                                                minute: '2-digit'
                                            })}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
                                <div className="flex justify-between items-center">
                                    <div>
                                        <p className="text-sm font-medium text-gray-700">Total Score</p>
                                        <p className="text-2xl font-bold text-gray-900">{result.cbt_total_score} Points</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-sm font-medium text-gray-700">Performance</p>
                                        <p className={`text-sm font-bold ${getScoreColor(result.cbt_total_score).includes('green') ? 'text-green-600' : getScoreColor(result.cbt_total_score).includes('yellow') ? 'text-yellow-600' : 'text-red-600'}`}>
                                            {getPerformanceText(result.cbt_total_score)}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div className="bg-gray-50 px-6 py-4 rounded-b-2xl">
                        <button
                            type="button"
                            className="w-full inline-flex justify-center items-center rounded-xl border border-gray-300 shadow-sm px-4 py-3 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                            onClick={onClose}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            Close Results
                        </button>
                    </div>
                </div>
            </div>

            <style jsx>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes scaleIn {
                    from { 
                        opacity: 0;
                        transform: scale(0.9) translateY(-10px);
                    }
                    to { 
                        opacity: 1;
                        transform: scale(1) translateY(0);
                    }
                }
                .animate-fade-in {
                    animation: fadeIn 0.3s ease-out;
                }
                .animate-scale-in {
                    animation: scaleIn 0.3s ease-out;
                }
            `}</style>
        </div>
    );
};

export default ResultDetailsModal;